import type { AutoBlockConfig, AutoRule } from '@/mocks/adminModeracion';

interface Props {
  config: AutoBlockConfig;
  onChange: (config: AutoBlockConfig) => void;
  rules: AutoRule[];
  onToggleRule: (id: string) => void;
}

export default function AutoBlockConfigPanel({ config, onChange, rules, onToggleRule }: Props) {
  const update = (partial: Partial<AutoBlockConfig>) => {
    onChange({ ...config, ...partial });
  };

  return (
    <div className="space-y-6">
      {/* Master toggle */}
      <div className="flex items-center justify-between bg-background-50 border border-background-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-background-200 text-foreground-400'}`}>
            <i className={`${config.enabled ? 'ri-shield-check-line' : 'ri-shield-line'} text-lg`}></i>
          </div>
          <div>
            <p className="text-sm font-bold text-foreground-900">Filtro automático</p>
            <p className="text-[10px] text-foreground-500">{config.enabled ? 'El sistema modera automáticamente los mensajes entrantes' : 'La moderación automática está desactivada'}</p>
          </div>
        </div>
        <button
          onClick={() => update({ enabled: !config.enabled })}
          className={`w-11 h-6 rounded-full relative cursor-pointer transition-colors ${config.enabled ? 'bg-emerald-500' : 'bg-foreground-300'}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 shadow-sm transition-transform ${config.enabled ? 'left-[22px]' : 'left-0.5'}`}></span>
        </button>
      </div>

      {/* Settings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <label className="block text-[10px] font-semibold text-foreground-500 mb-2">Advertencias antes de bloqueo</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => update({ warningsBeforeBlock: Math.max(1, config.warningsBeforeBlock - 1) })}
              disabled={!config.enabled}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-subtract-line text-foreground-600"></i>
            </button>
            <span className="text-xl font-bold text-foreground-900 w-8 text-center">{config.warningsBeforeBlock}</span>
            <button
              onClick={() => update({ warningsBeforeBlock: Math.min(10, config.warningsBeforeBlock + 1) })}
              disabled={!config.enabled}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-add-line text-foreground-600"></i>
            </button>
          </div>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <label className="block text-[10px] font-semibold text-foreground-500 mb-2">Tiempo de bloqueo (minutos)</label>
          <select
            value={config.blockDurationMinutes}
            onChange={(e) => update({ blockDurationMinutes: Number(e.target.value) })}
            disabled={!config.enabled}
            className="w-full px-3 py-2 rounded-xl bg-background-100 text-foreground-800 text-xs border border-background-200 focus:border-secondary-300 outline-none disabled:opacity-40"
          >
            <option value={15}>15 minutos</option>
            <option value={30}>30 minutos</option>
            <option value={60}>1 hora</option>
            <option value={120}>2 horas</option>
            <option value={360}>6 horas</option>
            <option value={1440}>24 horas</option>
          </select>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <label className="block text-[10px] font-semibold text-foreground-500 mb-2">Umbral de mensajes repetidos</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => update({ repeatedThreshold: Math.max(2, config.repeatedThreshold - 1) })}
              disabled={!config.enabled}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-subtract-line text-foreground-600"></i>
            </button>
            <span className="text-xl font-bold text-foreground-900 w-8 text-center">{config.repeatedThreshold}</span>
            <button
              onClick={() => update({ repeatedThreshold: Math.min(20, config.repeatedThreshold + 1) })}
              disabled={!config.enabled}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-add-line text-foreground-600"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Switches */}
      <div className="bg-background-50 border border-background-200 rounded-xl divide-y divide-background-100">
        {[
          { key: 'blockSpam', label: 'Bloqueo por spam', desc: 'Detecta y bloquea mensajes repetitivos y contenido no deseado', icon: 'ri-spam-2-line' },
          { key: 'blockLinks', label: 'Bloqueo por links', desc: 'Filtra mensajes que contengan enlaces externos sospechosos', icon: 'ri-links-line' },
          { key: 'blockInsults', label: 'Bloqueo por insultos', desc: 'Bloquea automáticamente mensajes con lenguaje ofensivo grave', icon: 'ri-emotion-unhappy-line' },
          { key: 'blockRepeated', label: 'Bloqueo por mensajes repetidos', desc: `Bloquea si un usuario repite el mismo mensaje más de ${config.repeatedThreshold} veces`, icon: 'ri-loop-left-line' },
        ].map((item) => (
          <div key={item.key} className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                <i className={`${item.icon} text-foreground-500 text-sm`}></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground-900">{item.label}</p>
                <p className="text-[10px] text-foreground-400">{item.desc}</p>
              </div>
            </div>
            <button
              onClick={() => update({ [item.key]: !config[item.key as keyof AutoBlockConfig] })}
              disabled={!config.enabled}
              className={`w-10 h-5.5 rounded-full relative cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                config[item.key as keyof AutoBlockConfig] ? 'bg-emerald-500' : 'bg-foreground-300'
              }`}
            >
              <span className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-background-50 shadow-sm transition-transform ${config[item.key as keyof AutoBlockConfig] ? 'left-[21px]' : 'left-0.5'}`}></span>
            </button>
          </div>
        ))}
      </div>

      {/* Auto Rules Cards */}
      <div>
        <h4 className="text-xs font-bold text-foreground-900 mb-3">Reglas automáticas activas</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className={`bg-background-50 border rounded-xl p-4 transition-all ${rule.enabled ? 'border-background-200' : 'border-background-100 opacity-60'}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${rule.enabled ? 'bg-secondary-100 text-secondary-600' : 'bg-background-200 text-foreground-400'}`}>
                  <i className={`${rule.icon} text-sm`}></i>
                </div>
                <button
                  onClick={() => onToggleRule(rule.id)}
                  disabled={!config.enabled}
                  className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                    rule.enabled ? 'bg-emerald-500' : 'bg-foreground-300'
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 shadow-sm transition-transform ${rule.enabled ? 'left-[18px]' : 'left-0.5'}`}></span>
                </button>
              </div>
              <p className="text-xs font-bold text-foreground-900">{rule.name}</p>
              <p className="text-[10px] text-foreground-500 mt-1 leading-relaxed">{rule.description}</p>
              <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-background-100">
                <i className="ri-bar-chart-2-line text-foreground-400 text-[10px]"></i>
                <span className="text-[10px] text-foreground-500">{rule.matchCount.toLocaleString()} coincidencias este mes</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}